import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LogOut, Users, FileText, Newspaper, BarChart3, Search,
  Edit3, Trash2, Plus, Save, X, Loader2, Clock, RefreshCw, Activity, GitMerge, Play, Download, Zap, Rocket, AlertTriangle,
} from 'lucide-react';
import api from '@/api';
import { LOGO_URL } from '@/constants/categories';
import { FeedMonitorTab } from '@/components/admin/FeedMonitorTab';
import UsersTab from '@/components/admin/UsersTab';
import { DataVolumetricsTab } from '@/components/admin/DataVolumetricsTab';
import { SchedulerTab } from '@/components/admin/SchedulerTabs';
import { DedupTab } from '@/components/admin/DedupTab';
import { DeployCheckTab } from '@/components/admin/DeployCheckTab';
import { NewsFeedTab } from '@/components/admin/NewsFeedTab';
import { PipelineTab } from '@/components/admin/PipelineTab';
import { PipelineErrorsTab } from '@/components/admin/PipelineErrorsTab';
import { DeployTab } from '@/components/admin/DeployToAwsTab';
import FunctionalSpec from '@/components/FunctionalSpec';

const ADMIN_TABS = [
  // Default landing: Users (first in user-requested sequence — April 2026)
  { key: 'users', label: 'Users', icon: Users },
  { key: 'prompts', label: 'AI Prompts', icon: FileText },
  { key: 'scheduler', label: 'Scheduler', icon: Clock },
  { key: 'feeds', label: 'Feed Monitor', icon: Activity },
  { key: 'topics', label: 'News Feed', icon: Newspaper },
  { key: 'pipeline', label: 'Pipeline Runs', icon: Zap },
  { key: 'pipeline_errors', label: 'Pipeline Errors', icon: AlertTriangle },
  { key: 'dedup', label: 'Matching Pipeline', icon: GitMerge },
  { key: 'volumetrics', label: 'Data Volume', icon: BarChart3 },
  { key: 'deploy_aws', label: 'Deploy AWS', icon: Rocket },
  { key: 'deploy', label: 'Deployment Check', icon: Activity },
  { key: 'spec', label: 'Spec', icon: FileText },
];

const CATEGORIES = [
  'technology', 'finance', 'ai', 'economy', 'crypto', 'world_news', 'internet_culture', 'politics', 'entertainment', 'lifestyle'
];

export default function AdminPanel({ onLogout }) {
  const [tab, setTab] = useState('users');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [editSystemPrompt, setEditSystemPrompt] = useState('');
  const [editTaskPrompt, setEditTaskPrompt] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [showAddTopic, setShowAddTopic] = useState(false);
  const [newTopic, setNewTopic] = useState({ title: '', category: 'technology', trend_score: 50 });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [runningPrompt, setRunningPrompt] = useState(null);
  const [promptResult, setPromptResult] = useState(null);
  const [schedulerData, setSchedulerData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [cleaningSpam, setCleaningSpam] = useState(false);
  const [regPaused, setRegPaused] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [feedData, setFeedData] = useState([]);
  const [feedSearch, setFeedSearch] = useState('');
  const [feedCategoryFilter, setFeedCategoryFilter] = useState('all');
  const [feedTypeFilter, setFeedTypeFilter] = useState('all');
  const [feedStatusFilter, setFeedStatusFilter] = useState('all');
  const [feedSort, setFeedSort] = useState({ key: 'label', dir: 'asc' });

  useEffect(() => {
    if (tab === 'overview') loadStats();
    if (tab === 'users') loadUsers();
    if (tab === 'prompts') loadPrompts();
    if (tab === 'topics') loadTopics();
    if (tab === 'scheduler') loadScheduler();
    if (tab === 'publisher') loadPublished();
    if (tab === 'feeds') loadFeedMonitor();
  }, [tab]);

  const loadStats = async () => { try { setStats(await api.adminGetStats()); } catch {} };
  const loadFeedMonitor = async () => {
    setLoading(true);
    try {
      const data = await api.adminGetFeedMonitor();
      setFeedData(data.feeds || []);
    } catch {
      // Retry once after a short delay (handles auth token race condition)
      await new Promise(r => setTimeout(r, 500));
      try { setFeedData((await api.adminGetFeedMonitor()).feeds || []); } catch {}
    } finally { setLoading(false); }
  };
  const loadUsers = async () => {
    setLoading(true);
    try {
      setUsers((await api.adminGetUsers()).users);
      const token = localStorage.getItem('wtf_admin_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/registration/status`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setRegPaused(data.registration_paused);
    } catch {} finally { setLoading(false); }
  };
  const loadPrompts = async () => { setLoading(true); try { setPrompts((await api.adminGetPrompts()).prompts); } catch {} finally { setLoading(false); } };
  const loadTopics = async () => { setLoading(true); try { setTopics((await api.adminGetTopics()).topics); } catch {} finally { setLoading(false); } };
  const loadScheduler = async () => { setLoading(true); try { setSchedulerData(await api.adminGetScheduler()); } catch {} finally { setLoading(false); } };
  const loadPublished = async () => {};

  const showMsg = (msg) => { setMessage(msg); setTimeout(() => setMessage(''), 3000); };

  const handleUpdateUserStatus = async (userId, status) => {
    let reason = '';
    // Prompt for reason only on transitions into suspended/banned (user-facing email goes out)
    if (status === 'suspended' || status === 'banned') {
      const prompted = window.prompt(`Why are you ${status === 'suspended' ? 'suspending' : 'banning'} this user? (shown in notification email)`);
      if (prompted === null) return; // cancelled
      reason = prompted.trim();
    }
    try {
      const token = localStorage.getItem('wtf_admin_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/status?status=${encodeURIComponent(status)}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      if (!res.ok) throw new Error('status update failed');
      const data = await res.json();
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, status } : u));
      const emailStatus = data?.email_notification;
      if (status === 'suspended' || status === 'banned') {
        showMsg(`User ${status} · email ${emailStatus === 'sent' ? 'sent' : (emailStatus || 'skipped')}`);
      } else {
        showMsg(`User reactivated`);
      }
    } catch { showMsg('Failed to update status'); }
  };

  const handleSavePrompt = async (prompt) => {
    setSaving(true);
    try {
      await api.adminUpdatePrompt(prompt.id, { prompt_key: prompt.prompt_key, system_prompt: editSystemPrompt, task_prompt: editTaskPrompt });
      setPrompts(prev => prev.map(p => p.id === prompt.id ? { ...p, system_prompt: editSystemPrompt, task_prompt: editTaskPrompt } : p));
      setEditingPrompt(null);
      showMsg('Prompt updated');
    } catch {} finally { setSaving(false); }
  };

  const handleAddTopic = async () => {
    if (!newTopic.title.trim()) return;
    setSaving(true);
    try {
      const res = await api.adminCreateTopic(newTopic);
      setTopics(prev => [res.topic, ...prev]);
      setNewTopic({ title: '', category: 'technology', trend_score: 50 });
      setShowAddTopic(false);
      showMsg('Topic added');
    } catch {} finally { setSaving(false); }
  };

  const handleDeleteTopic = async (topicId) => {
    try { await api.adminDeleteTopic(topicId); setTopics(prev => prev.filter(t => t.id !== topicId)); showMsg('Topic deleted'); } catch {}
  };

  const handleManualRefresh = async () => {
    setRefreshing(true);
    try { await api.refreshTrending(); showMsg('Data refresh triggered'); setTimeout(loadScheduler, 2000); } catch {} finally { setRefreshing(false); }
  };

  const handleTriggerJob = async (jobId) => {
    try {
      await api.adminTriggerJob(jobId);
      showMsg(`Job "${jobId}" triggered`);
      setTimeout(loadScheduler, 3000);
    } catch { showMsg('Failed to trigger job'); }
  };

  const wildcardMatch = (text, pattern) => {
    if (!pattern) return true;
    const regex = new RegExp('^' + pattern.replace(/\*/g, '.*').replace(/\?/g, '.') + '$', 'i');
    return regex.test(text);
  };

  const handleToggleRegistration = async () => {
    const action = regPaused ? 'resume' : 'pause';
    if (!regPaused && !window.confirm('Pause all new registrations?\n\nNew users trying to sign up will see:\n"We are currently undergoing maintenance. Kindly try later."\n\nExisting users can still log in.')) return;
    try {
      const token = localStorage.getItem('wtf_admin_token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/registration/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      setRegPaused(!regPaused);
      showMsg(regPaused ? 'Registration resumed' : 'Registration paused');
    } catch { showMsg('Failed to toggle'); }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Delete user "${userName}"?\n\nThis will anonymise their account and send them a deletion-notice email. Past transactions (invoices) are retained for compliance.`)) return;
    const reason = window.prompt('Reason for deletion (shown in notification email, optional):') || '';
    try {
      const token = localStorage.getItem('wtf_admin_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        const emailStatus = data?.email_notification;
        showMsg(`Deleted ${userName} · email ${emailStatus === 'sent' ? 'sent' : (emailStatus || 'skipped')}`);
        loadUsers();
      } else {
        showMsg(data.detail || 'Failed to delete');
      }
    } catch { showMsg('Failed to delete user'); }
  };

  const handleMassDelete = async () => {
    if (selectedUsers.size === 0) return;
    if (!window.confirm(`Delete ${selectedUsers.size} selected users? This cannot be undone.`)) return;
    setCleaningSpam(true);
    try {
      const token = localStorage.getItem('wtf_admin_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/mass-delete`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_ids: [...selectedUsers] }),
      });
      const data = await res.json();
      showMsg(`Deleted ${data.deleted} users`);
      setSelectedUsers(new Set());
      loadUsers();
    } catch { showMsg('Failed to delete'); }
    finally { setCleaningSpam(false); }
  };

  const filteredUsers = users.filter(u => {
    if (!userSearch) return true;
    const q = userSearch.trim();
    const hasWildcard = q.includes('*') || q.includes('?');
    if (hasWildcard) {
      return wildcardMatch(u.name || '', q) || wildcardMatch(u.email || '', q) || wildcardMatch(u.mobile || '', q);
    }
    return ((u.name || '') + (u.email || '') + (u.mobile || '')).toLowerCase().includes(q.toLowerCase());
  });

  const nonAdminFiltered = filteredUsers.filter(u => u.role !== 'admin');
  const allSelected = nonAdminFiltered.length > 0 && nonAdminFiltered.every(u => selectedUsers.has(u.id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(nonAdminFiltered.map(u => u.id)));
    }
  };

  const toggleSelectUser = (id) => {
    setSelectedUsers(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <div data-testid="admin-panel" className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-100 px-5 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-2">
          <img src={LOGO_URL} alt="WTFHappened" className="h-14 object-contain" />
          <span className="text-sm font-bold text-gray-900">Admin</span>
        </div>
        <button data-testid="admin-logout" onClick={onLogout} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-500 hover:bg-gray-100 transition-colors">
          <LogOut size={14} /> Logout
        </button>
      </div>

      <div className="flex gap-1.5 px-5 py-3 overflow-x-auto">
        {ADMIN_TABS.map(t => (
          <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all ${tab === t.key ? 'bg-gray-900 text-white' : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-200'}`}>
            <t.icon size={14} />{t.label}
          </button>
        ))}
      </div>

      <AnimatePresence>
        {message && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="mx-5 mb-3 px-4 py-2 rounded-xl bg-green-50 text-green-700 text-xs font-medium text-center">{message}</motion.div>
        )}
      </AnimatePresence>

      <div className="px-5 pb-10">
        {tab === 'feeds' && (
          <FeedMonitorTab feedData={feedData} loading={loading} loadFeedMonitor={loadFeedMonitor}
            feedSearch={feedSearch} setFeedSearch={setFeedSearch} feedCategoryFilter={feedCategoryFilter} setFeedCategoryFilter={setFeedCategoryFilter}
            feedTypeFilter={feedTypeFilter} setFeedTypeFilter={setFeedTypeFilter} feedStatusFilter={feedStatusFilter} setFeedStatusFilter={setFeedStatusFilter}
            feedSort={feedSort} setFeedSort={setFeedSort} />
        )}

        {tab === 'users' && (
          <UsersTab
            users={users}
            filteredUsers={filteredUsers}
            nonAdminFiltered={nonAdminFiltered}
            userSearch={userSearch}
            setUserSearch={setUserSearch}
            regPaused={regPaused}
            handleToggleRegistration={handleToggleRegistration}
            selectedUsers={selectedUsers}
            setSelectedUsers={setSelectedUsers}
            toggleSelectAll={toggleSelectAll}
            allSelected={allSelected}
            toggleSelectUser={toggleSelectUser}
            handleMassDelete={handleMassDelete}
            handleUpdateUserStatus={handleUpdateUserStatus}
            handleDeleteUser={handleDeleteUser}
            cleaningSpam={cleaningSpam}
            loading={loading}
          />
        )}

        {tab === 'prompts' && (
          <div className="mt-2 space-y-3">
            <p className="text-xs text-gray-400 mb-1">3-step AI pipeline: Fact Extraction &#8594; Driver Analysis &#8594; Card Generation</p>
            {loading ? <div className="text-center py-10"><Loader2 size={24} className="animate-spin mx-auto text-gray-400" /></div> : (
              prompts.map((p, idx) => (
                <div key={p.id} className="bg-white rounded-xl border border-gray-100 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-sm font-bold text-gray-900">{p.label || p.prompt_key}</p>
                      {p.description && <p className="text-[10px] text-gray-400 mt-0.5">{p.description}</p>}
                    </div>
                    {editingPrompt === p.id ? (
                      <div className="flex gap-1">
                        <button onClick={() => setEditingPrompt(null)} className="p-1 rounded-lg hover:bg-gray-100"><X size={14} /></button>
                        <button data-testid={`save-prompt-${idx}`} onClick={() => handleSavePrompt(p)} disabled={saving} className="p-1 rounded-lg hover:bg-green-100 text-green-600">
                          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-1">
                        <button data-testid={`run-prompt-${idx}`}
                          onClick={async () => {
                            setRunningPrompt(p.id);
                            setPromptResult(null);
                            try {
                              const res = await api.adminRunPrompt(p.id);
                              setPromptResult({ id: p.id, ...res });
                            } catch (e) {
                              setPromptResult({ id: p.id, success: false, error: e.message });
                            }
                            setRunningPrompt(null);
                          }}
                          disabled={runningPrompt === p.id}
                          className="px-2.5 py-1 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 text-[11px] font-bold flex items-center gap-1 transition-colors"
                        >
                          {runningPrompt === p.id ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
                          Run
                        </button>
                        <button data-testid={`edit-prompt-${idx}`} onClick={() => { setEditingPrompt(p.id); setEditSystemPrompt(p.system_prompt || ''); setEditTaskPrompt(p.task_prompt || ''); }}
                          className="p-1 rounded-lg hover:bg-gray-100"><Edit3 size={14} className="text-gray-400" /></button>
                      </div>
                    )}
                  </div>
                  {editingPrompt === p.id ? (
                    <div className="space-y-3">
                      <div>
                        <label className="text-[10px] font-bold text-gray-500 tracking-widest uppercase block mb-1">System Prompt</label>
                        <textarea data-testid={`system-prompt-${idx}`} value={editSystemPrompt} onChange={e => setEditSystemPrompt(e.target.value)}
                          rows={5} className="w-full text-xs bg-gray-50 border border-gray-200 rounded-lg p-3 outline-none focus:border-gray-400 font-mono resize-y" />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-gray-500 tracking-widest uppercase block mb-1">Task Prompt</label>
                        <textarea data-testid={`task-prompt-${idx}`} value={editTaskPrompt} onChange={e => setEditTaskPrompt(e.target.value)}
                          rows={8} className="w-full text-xs bg-gray-50 border border-gray-200 rounded-lg p-3 outline-none focus:border-gray-400 font-mono resize-y" />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div><p className="text-[9px] font-bold text-gray-400 tracking-widest uppercase mb-0.5">System Prompt</p><p className="text-xs text-gray-500 font-mono whitespace-pre-wrap line-clamp-3">{p.system_prompt}</p></div>
                      <div><p className="text-[9px] font-bold text-gray-400 tracking-widest uppercase mb-0.5">Task Prompt</p><p className="text-xs text-gray-500 font-mono whitespace-pre-wrap line-clamp-3">{p.task_prompt}</p></div>
                    </div>
                  )}
                  {/* Run result */}
                  {promptResult && promptResult.id === p.id && (
                    <div className={`mt-3 p-3 rounded-lg text-xs font-mono ${promptResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}
                      data-testid={`prompt-result-${idx}`}>
                      <p className="font-bold mb-1" style={{ color: promptResult.success ? '#16a34a' : '#dc2626' }}>
                        {promptResult.success ? 'Success' : 'Error'}
                      </p>
                      {promptResult.test_topic && <p className="text-gray-500 mb-1">Topic: {promptResult.test_topic}</p>}
                      <pre className="whitespace-pre-wrap text-[10px] text-gray-600 max-h-48 overflow-auto">
                        {promptResult.error || JSON.stringify(promptResult.result, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {tab === 'topics' && (
          <NewsFeedTab onDeleteTopic={handleDeleteTopic} />
        )}

        {tab === 'scheduler' && <SchedulerTab schedulerData={schedulerData} loading={loading} refreshing={refreshing} onManualRefresh={handleManualRefresh} onTriggerJob={handleTriggerJob} />}
        {tab === 'dedup' && <DedupTab />}
        {tab === 'pipeline' && <PipelineTab />}
        {tab === 'pipeline_errors' && <PipelineErrorsTab />}
        {tab === 'deploy' && <DeployCheckTab />}
        {tab === 'deploy_aws' && <DeployTab />}
        {tab === 'volumetrics' && <DataVolumetricsTab />}
        {tab === 'spec' && (
          <div className="mt-2">
            <div className="flex justify-end gap-2 mb-3">
              <button data-testid="download-tech-spec-pdf" onClick={async () => {
                try {
                  const API = process.env.REACT_APP_BACKEND_URL;
                  const url = `${API}/api/static/WTFHappened_Technical_Specification.pdf`;
                  // Static asset — no auth header needed; just open the download.
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'WTFHappened_Technical_Specification.pdf';
                  a.target = '_blank';
                  a.rel = 'noopener';
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                } catch (e) { console.error('Download failed:', e); }
              }}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 transition-colors">
                <Download size={14} /> Download Tech Spec PDF
              </button>
              <button data-testid="download-data-sources" onClick={async () => {
                try {
                  const blob = await api.adminDownloadDataSources();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = 'wtfhappened_data_sources.csv'; a.click();
                  URL.revokeObjectURL(url);
                } catch (e) { console.error('Download failed:', e); }
              }}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-900 text-white text-xs font-bold hover:bg-gray-800 transition-colors">
                <Download size={14} /> Download Data Sources CSV
              </button>
            </div>
            <FunctionalSpec embedded />
          </div>
        )}
      </div>
    </div>
  );
}
